import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import RoleShell from '../../components/Enrollment/RoleShell';
import { Prod01 } from '../../constants/ActionType';
import { fetchMyOrganizations } from '../../utils/useMyOrganizations';
import { useSortableTable, SortableTh, nameFromString } from '../../components/SortableTable';

// Org Manager worklist: patients without complete, active insurance (blocked from $ forecasts and device-request approval).
const STATE_LABEL = { missing: 'Insurance missing', incomplete: 'Incomplete', expired: 'Expired' };
const MISSING_SORT_COLUMNS = {
    patient: { type: 'name', value: (r) => nameFromString(r?.patientName) },
    emrId: { type: 'string', value: (r) => r?.emrId },
    practice: { type: 'string', value: (r) => r?.practice },
    status: { type: 'string', value: (r) => r?.label },
};
const errText = (e) => e?.response?.data?.message || e?.message || 'Request failed';

const InsuranceMissing = () => {
    const [orgs, setOrgs] = useState(null);
    const [orgId, setOrgId] = useState('');
    const [state, setState] = useState('all');
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => { fetchMyOrganizations().then((d) => setOrgs(d)); }, []);
    const load = useCallback(async () => {
        setError(null);
        try { setData((await axios.get(`${Prod01}/insurance/missing${orgId ? `?organizationId=${orgId}` : ''}`)).data); } catch (e) { setError(errText(e)); setData(null); }
    }, [orgId]);
    useEffect(() => { load(); }, [load]);

    const orgList = orgs?.organizations || [];
    const filtered = (data?.rows || []).filter((r) => state === 'all' || r.state === state);
    const tableSort = useSortableTable(filtered, { id: 'insurance.missing', columns: MISSING_SORT_COLUMNS, defaultSort: { key: 'patient', dir: 'asc' } });
    const rows = tableSort.rows;
    return (
        <RoleShell title="Insurance missing">
            <div className="container-fluid mt-3" data-testid="insurance-missing">
                <div className="d-flex flex-wrap align-items-center mb-2">
                    <h5 className="mb-0 mr-3">Insurance missing</h5>
                    {(orgs?.global || orgList.length > 1) && (
                        <select aria-label="Practice" className="form-control form-control-sm" style={{ width: 240 }} value={orgId} onChange={(e) => setOrgId(e.target.value)}>
                            <option value="">{orgs?.global ? 'All practices' : 'All my practices'}</option>
                            {orgList.map((o) => <option key={o._id} value={o._id}>{o.name}</option>)}
                        </select>
                    )}
                </div>
                <p className="text-muted" style={{ fontSize: 13 }}>
                    These patients show &quot;Blocked — missing insurance&quot;: they are counted but excluded from dollar forecasts, and their device requests cannot be approved. Open the patient profile and complete the Insurance section.
                </p>
                {error && <div className="alert alert-danger" role="alert">{error}</div>}
                {data && (
                    <>
                        <div className="mb-2" style={{ fontSize: 13 }}>
                            {['all', 'missing', 'incomplete', 'expired'].map((k) => (
                                <button type="button" key={k} className={`btn btn-sm mr-1 ${state === k ? 'btn-primary' : 'btn-light'}`} onClick={() => setState(k)}>
                                    {k === 'all' ? `All (${data.total})` : `${STATE_LABEL[k]} (${data.counts[k] || 0})`}
                                </button>
                            ))}
                        </div>
                        <table className="table table-sm" style={{ fontSize: 13 }} data-testid="ins-missing-table">
                            <thead><tr><SortableTh sortKey="patient" sort={tableSort}>Patient</SortableTh><SortableTh sortKey="emrId" sort={tableSort}>EHR ID</SortableTh><SortableTh sortKey="practice" sort={tableSort}>Practice</SortableTh><SortableTh sortKey="status" sort={tableSort}>Status</SortableTh><th>What is missing</th></tr></thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.patientId} data-testid="ins-missing-row">
                                        <td><Link to={{ pathname: '/patientprofile', state: { patientid: r.patientId } }}>{r.patientName}</Link></td>
                                        <td>{r.emrId || '—'}</td>
                                        <td>{r.practice || '—'}</td>
                                        <td>{r.label}</td>
                                        <td>{r.missing.join(', ') || '—'}</td>
                                    </tr>
                                ))}
                                {!rows.length && <tr><td colSpan={5} className="text-center text-muted">Every patient in this view has complete, active insurance.</td></tr>}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
        </RoleShell>
    );
};

export default InsuranceMissing;
